import { useMemo, useCallback } from 'react';

interface PianoKeyboardProps {
  expectedNotes: Set<number>;
  /** OTO-23: staff (1=right, 2=left) for each pitch in `expectedNotes`, so
   * key glow can be colored by hand instead of by pitch. Only covers
   * expected (score) notes -- `playedNotes` come from live MIDI input,
   * which has no staff concept, and are intentionally not represented here. */
  expectedStaffByPitch: Map<number, 1 | 2>;
  playedNotes: Set<number>;
  onNoteOn: (pitch: number) => void;
  onNoteOff: (pitch: number) => void;
  width: number;
  height: number;
  /** First MIDI note to display (default: 21 = A0) */
  startNote?: number;
  /** Last MIDI note to display (default: 108 = C8) */
  endNote?: number;
}

// Which notes in an octave are white keys (0 = C)
const WHITE_KEY_PATTERN = [0, 2, 4, 5, 7, 9, 11];

// Note names for display
const NOTE_NAMES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];

/**
 * Interactive piano keyboard component
 * Highlights expected notes (from score) and played notes (from MIDI input)
 */
export function PianoKeyboard({
  expectedNotes,
  expectedStaffByPitch,
  playedNotes,
  onNoteOn,
  onNoteOff,
  width,
  height,
  startNote = 21,
  endNote = 108,
}: PianoKeyboardProps) {
  // Calculate key layout
  const keyLayout = useMemo(() => {
    const layout: {
      white: Array<{ midi: number; x: number; width: number; note: string }>;
      black: Array<{ midi: number; x: number; width: number; note: string }>;
    } = { white: [], black: [] };

    // Count white keys
    let whiteKeyCount = 0;
    for (let midi = startNote; midi <= endNote; midi++) {
      const noteInOctave = midi % 12;
      if (WHITE_KEY_PATTERN.includes(noteInOctave)) {
        whiteKeyCount++;
      }
    }

    const whiteKeyWidth = width / whiteKeyCount;
    const blackKeyWidth = whiteKeyWidth * 0.6;
    const blackKeyHeight = height * 0.6;

    let whiteKeyIndex = 0;

    for (let midi = startNote; midi <= endNote; midi++) {
      const noteInOctave = midi % 12;
      const octave = Math.floor(midi / 12) - 1;
      const noteName = NOTE_NAMES[noteInOctave];
      const fullName = `${noteName}${octave}`;

      if (WHITE_KEY_PATTERN.includes(noteInOctave)) {
        layout.white.push({
          midi,
          x: whiteKeyIndex * whiteKeyWidth,
          width: whiteKeyWidth,
          note: fullName,
        });
        whiteKeyIndex++;
      } else {
        // Position black key relative to the white key before it
        const x = (whiteKeyIndex - 1) * whiteKeyWidth + whiteKeyWidth - blackKeyWidth / 2;
        layout.black.push({
          midi,
          x,
          width: blackKeyWidth,
          note: fullName,
        });
      }
    }

    return { ...layout, blackKeyHeight };
  }, [width, height, startNote, endNote]);

  // Mouse event handlers for virtual keyboard
  const handleMouseDown = useCallback(
    (midi: number) => {
      onNoteOn(midi);
    },
    [onNoteOn]
  );

  const handleMouseUp = useCallback(
    (midi: number) => {
      onNoteOff(midi);
    },
    [onNoteOff]
  );

  /**
   * Key color by hand STATE (right/left/neutral), never by midi/pitch.
   * 'neutral' is a third, distinct color (gray) for played-only keys with
   * no staff data -- not a guessed hand and not the removed pitch heuristic.
   */
  const getHandColor = (state: 'right' | 'left' | 'neutral', type: 'bg' | 'shadow' | 'glow') => {
    if (state === 'left') {
      if (type === 'bg') return '#22c55e';
      if (type === 'shadow') return 'rgba(34, 197, 94, 0.6)';
      return 'rgba(74, 222, 128, 0.5)';
    }
    if (state === 'neutral') {
      if (type === 'bg') return '#9ca3af';
      if (type === 'shadow') return 'rgba(156, 163, 175, 0.6)';
      return 'rgba(209, 213, 219, 0.5)';
    }
    if (type === 'bg') return '#3b82f6';
    if (type === 'shadow') return 'rgba(59, 130, 246, 0.6)';
    return 'rgba(96, 165, 250, 0.5)';
  };

  /**
   * Resolve one key's color state. A pitch expected on both staves at once
   * was already tie-broken to staff 1 by `computeExpectedHandMap` (right
   * wins) before this ever runs, so 'right' is also the correct default
   * here if a pitch is somehow active without a map entry (defense in
   * depth, not the primary tie-break site). `playedNotes` (live MIDI) carry
   * no staff data at all -- always 'neutral', never guessed.
   */
  const keyColorState = (midi: number, isExpected: boolean): 'right' | 'left' | 'neutral' => {
    if (isExpected) return expectedStaffByPitch.get(midi) === 2 ? 'left' : 'right';
    return 'neutral';
  };

  return (
    <div className="relative select-none" style={{ width, height }}>
      {/* White keys */}
      {keyLayout.white.map((key) => {
        const isExpected = expectedNotes.has(key.midi);
        const isPlayed = playedNotes.has(key.midi);
        const isActive = isExpected || isPlayed;
        const colorState = keyColorState(key.midi, isExpected);
        const handBg = getHandColor(colorState, 'bg');

        return (
          <div
            key={key.midi}
            data-midi={key.midi}
            className="absolute top-0 border border-gray-300 rounded-b cursor-pointer transition-all duration-75"
            style={{
              left: key.x,
              width: key.width - 1,
              height: height,
              backgroundColor: isActive ? handBg : '#ffffff',
              boxShadow: isActive
                ? `inset 0 0 20px ${getHandColor(colorState, 'glow')}, 0 0 12px ${getHandColor(colorState, 'shadow')}`
                : undefined,
            }}
            onMouseDown={() => handleMouseDown(key.midi)}
            onMouseUp={() => handleMouseUp(key.midi)}
            onMouseLeave={() => handleMouseUp(key.midi)}
          >
            {key.note.startsWith('C') && (
              <span
                className="absolute bottom-2 left-1/2 -translate-x-1/2 text-xs font-medium"
                style={{ color: isActive ? '#ffffff' : '#6b7280' }}
              >
                {key.note}
              </span>
            )}
          </div>
        );
      })}

      {/* Black keys */}
      {keyLayout.black.map((key) => {
        const isExpected = expectedNotes.has(key.midi);
        const isPlayed = playedNotes.has(key.midi);
        const isActive = isExpected || isPlayed;
        const colorState = keyColorState(key.midi, isExpected);
        const handBg = getHandColor(colorState, 'bg');

        return (
          <div
            key={key.midi}
            data-midi={key.midi}
            className="absolute top-0 rounded-b cursor-pointer transition-all duration-75 z-10"
            style={{
              left: key.x,
              width: key.width,
              height: keyLayout.blackKeyHeight,
              backgroundColor: isActive ? handBg : '#1a1a1a',
              boxShadow: isActive
                ? `0 0 16px ${getHandColor(colorState, 'shadow')}, inset 0 0 10px ${getHandColor(colorState, 'glow')}`
                : '2px 2px 4px rgba(0,0,0,0.3)',
            }}
            onMouseDown={() => handleMouseDown(key.midi)}
            onMouseUp={() => handleMouseUp(key.midi)}
            onMouseLeave={() => handleMouseUp(key.midi)}
          />
        );
      })}
    </div>
  );
}
